import React from 'react';
import { Info } from 'lucide-react';

/**
 * Shared disclaimer block used wherever a bundle / configured price is shown:
 *   • BundlesBlock CTA hover-card on the home page
 *   • Configurator cart aside (sticky summary)
 *   • Configurator success screen
 *
 * The strings come from prices.json → bundles.labels.{whiteStandard,vatNote,
 * shippingNote} so editing the JSON updates every surface at once.
 *
 * Variants:
 *   • "card"   — bright cream tile with icon + title (used in hover-card, success).
 *   • "inline" — small dim line, no icon (used in dark cart aside).
 *
 * Pass `showWhiteStandard={false}` from contexts where the user has already
 * chosen a non-default color/mesh (configurator cart) — that note only
 * applies to bundle pricing.
 */
export default function BundleDisclaimer({
  labels,
  variant = 'card',
  showWhiteStandard = true,
  className = '',
  title = null,
  'data-testid': testId,
}) {
  const w = showWhiteStandard ? labels?.whiteStandardNote : null;
  const v = labels?.vatNote;
  const s = labels?.shippingNote;
  if (!w && !v && !s) return null;

  if (variant === 'inline') {
    return (
      <div
        data-testid={testId}
        className={`text-[10.5px] leading-snug space-y-1 ${className}`}
      >
        {w && <p className="opacity-80">* {w}</p>}
        {(v || s) && (
          <p className="opacity-70 tabular-nums">
            {v}{v && s ? ' · ' : ''}{s}
          </p>
        )}
      </div>
    );
  }

  // card variant
  return (
    <div
      data-testid={testId}
      className={`rounded-xl bg-[#FFF4E6] border border-[#F26B1A]/25 p-3.5 text-[12px] leading-relaxed text-[#1F1D1A] ${className}`}
    >
      {title && (
        <p className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#F26B1A] mb-2">
          <Info className="w-3.5 h-3.5" />
          {title}
        </p>
      )}
      {w && (
        <p className="mb-1.5">
          <span className="font-semibold">*</span> {w}
        </p>
      )}
      {(v || s) && (
        <p className="text-[#5C5852] tabular-nums">
          {v}{v && s ? ' · ' : ''}{s}
        </p>
      )}
    </div>
  );
}
